import i18n, { translateBackend } from '@assets/i18n/config';
import dayjs from 'dayjs';

import type { PortfolioReport, RepCodeBasic, SubAccountObject } from '../types';
import { getPortfolioStatus } from './index';

export const DASH = '—';

export function formatReasonLabel(report: PortfolioReport): string {
  return getPortfolioStatus(report).label;
}

export function formatModelNameLabel(report: PortfolioReport): string {
  return translateBackend(report.modelPortfolio?.translatedName) || DASH;
}

export function formatCustodianLabel(report: PortfolioReport): string {
  if (report.modelPortfolioType === 'SUB_ACCOUNT') {
    const account = (report.object as SubAccountObject).account;
    return account?.custodianAccountName ?? DASH;
  }

  const names = new Set<string>();
  for (const subAcct of report.subAccounts ?? []) {
    const name = subAcct.subAccount.account?.custodianAccountName;
    if (name) {
      names.add(name);
    }
  }

  if (names.size === 0) return DASH;
  if (names.size === 1) return [...names][0];
  return i18n.t('portfolioTradingHub:portfoliosTable.custodian.multiple');
}

export function getAdvisorTooltip(
  repCode?: RepCodeBasic | null,
): string | undefined {
  if (!repCode) return undefined;
  if (repCode.code && repCode.name) {
    return `${repCode.code} – ${repCode.name}`;
  }
  return repCode.name ?? repCode.code ?? undefined;
}

function getDistinctRepCodes(report: PortfolioReport): Set<string> {
  const codes = new Set<string>();
  for (const subAcct of report.subAccounts ?? []) {
    const code = subAcct.subAccount.account?.repCode?.code;
    if (code) {
      codes.add(code);
    }
  }
  return codes;
}

export function formatAdvisorLabel(
  report: PortfolioReport,
  { canReadRepCode }: { canReadRepCode: boolean },
): string {
  if (!canReadRepCode) return DASH;

  if (report.modelPortfolioType === 'SUB_ACCOUNT') {
    const repCode = (report.object as SubAccountObject).account?.repCode;
    return repCode?.code ?? DASH;
  }

  const codes = getDistinctRepCodes(report);
  if (codes.size === 0) return DASH;
  if (codes.size === 1) return [...codes][0];
  return i18n.t('portfolioTradingHub:portfoliosTable.advisor.multiple');
}

export function getAdvisorTooltipForReport(
  report: PortfolioReport,
  { canReadRepCode }: { canReadRepCode: boolean },
): string | undefined {
  if (!canReadRepCode) return undefined;

  if (report.modelPortfolioType === 'SUB_ACCOUNT') {
    return getAdvisorTooltip(
      (report.object as SubAccountObject).account?.repCode,
    );
  }

  const codes = getDistinctRepCodes(report);
  if (codes.size !== 1) return undefined;

  for (const subAcct of report.subAccounts ?? []) {
    const repCode = subAcct.subAccount.account?.repCode;
    if (repCode?.code && codes.has(repCode.code)) {
      return getAdvisorTooltip(repCode);
    }
  }

  return undefined;
}

export function formatLastRebalDateLabel(report: PortfolioReport): string {
  const createdAt = report.lastOptimizedBy?.createdAt;
  if (!createdAt) return DASH;
  return dayjs(createdAt).format('YYYY-MM-DD');
}
